import { Injectable, inject } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { UserService } from '../user/user.service';
import { GameProvidersService } from './game-providers.service';
import { Observable, ReplaySubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { GameCategoryAttrService, GameCategoryType } from './game-category-attribute.service';
import { UntilDestroy } from 'ngx-unificator/decorator';
import { untilDestroyed } from 'ngx-unificator/rxjs';
import { FiltersService } from '../filters.service';
import { GameCategory } from './game-category';
import { isArray } from 'ngx-unificator/helpers';

export interface Filters {
  categories?: number[]; // array of categories id
  providers?: number[]; // array of providers id
  attributes?: number[]; // array of attributes id
  search?: string; // search string
  extra?: string; // search string
}

export interface Option {
  value: number;
  label: string;
}

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class GameFilterService {
  user = inject(UserService);
  private _fb = inject(FormBuilder);
  private _router = inject(Router);
  private _category = inject(GameCategoryAttrService);
  private _providers = inject(GameProvidersService);
  private _filtersService = inject(FiltersService);

  /**
   * Form for filters
   */
  public form: FormGroup = this._fb.group({
    'category[]': [],
    'provider[]': [],
    'attribute[]': [],
  });

  /**
   * Create categories options for multi checkbox
   */
  public categories$: Observable<Option[]> = this._createCategoriesOptions();

  /**
   * Create providers options for multi checkbox
   */
  public providers$: Observable<Option[]> = this._createProvidersOptions();

  /**
   * Emit on filters change
   */
  public filtersChanged$: ReplaySubject<any> = new ReplaySubject<any>(1);

  /**
   * Hold current filters
   */
  public _filters: Filters = {
    categories: [],
    providers: [],
    attributes: [],
    search: '',
  };

  /**
   * Filters subject
   * @private
   */
  private _filters$: ReplaySubject<Filters> = new ReplaySubject<Filters>(1);

  /**
   * List of categories that require non default processing
   */
  public extraCategories: GameCategory[] = [GameCategory.LAST_PLAYED, GameCategory.FAVORITE];

  constructor() {
    this.filter$.pipe(untilDestroyed(this)).subscribe(() => this._settAppliedFilters());
  }

  /**
   * Access to current filters
   */
  get filter() {
    return this._filters;
  }

  set Filter(value: Filters | any) {
    this._filters = value;
  }

  /**
   * Access to current filters
   */
  get filter$() {
    return this._filters$;
  }

  /**
   * Get all attributes from categories data
   */
  public getAttributesFromCategories() {
    if (this.form.value['category[]']) {
      return this._filtersService.uniqueValuesByKey(
        'id',
        this._filtersService.includeUsedByKey('id', this.form.value['category[]'], this._category.attrList),
      );
    }
  }

  /**
   * Remove all attributes from categories data
   */
  public removeAttributesFromCategories() {
    if (this.form.value['category[]']) {
      return this._filtersService.uniqueValuesByKey(
        'id',
        this._filtersService.includeUsedByKey('id', this.form.value['category[]'], this._category.categoryList),
      );
    }
  }

  /**
   * Set filter form data for apply filters
   */
  public setFormDataForFilters() {
    return {
      categories: this.removeAttributesFromCategories(),
      providers: this.form.value['provider[]'] || [],
      attributes: this.getAttributesFromCategories(),
    };
  }

  /**
   * Apply new filters by navigating with params
   * @param newFiltersParams
   */
  public applyFilters(newFiltersParams): Promise<any> {
    const expectedFilters: Filters = {
      ...this._filters,
      ...newFiltersParams,
    };

    switch (true) {
      case this._isSingleCategoryApplied(expectedFilters):
        return this._router.navigate(['/', 'games', this._categoriesSlugsFromIds(expectedFilters.categories)[0]]);
      case this._isSingleProviderApplied(expectedFilters):
        return this._router.navigate(['/', 'games', this._providersSlugsFromIds(expectedFilters.providers)[0]]);
      case this._isSingleAttributeApplied(expectedFilters):
        return this._router.navigate(['/', 'games', this._attributesSlugsFromIds(expectedFilters.attributes)[0]]);
      default:
        const newQueryParams: any = this._resolveParamsForMultipleChoice(expectedFilters);
        return this._router.navigate(['/', 'games'], this._resolveParamsForSingleChoice(newQueryParams));
    }
  }

  /**
   * Resolve category or provider by slug and return the object with detected param
   * @param slug
   * @param query
   */
  public resolveCategoryOrProvider(slug, query) {
    if (this._categoriesIdsFromSlugs([slug]).length) {
      return { category: slug };
    } else if (this._providersIdsFromSlugs([slug]).length) {
      return { provider: slug };
    } else if (this._attributesIdsFromSlugs([slug]).length) {
      return { attribute: slug };
    } else {
      return null;
    }
  }

  /**
   * Resolve filter params for request from current filters
   */
  public resolveFiltersForRequest() {
    const filterForRequest = {};

    if (this.filter?.search) {
      (filterForRequest as any).search = this.filter.search;
    }

    if (this.filter?.providers?.length) {
      filterForRequest['provider[]'] = this.filter.providers;
    }

    if (this.filter?.categories?.length) {
      filterForRequest['category_in[]'] = this.filter.categories;
    }

    if (this.filter?.attributes?.length) {
      filterForRequest['attribute[]'] = this.filter.attributes;
    }

    return filterForRequest;
  }

  /**
   * Convert route params to filters object
   * @param params
   */
  public filtersFromRouteParams(params): Filters {
    const newFilters: Filters = {
      categories: [],
      providers: [],
      attributes: [],
      search: '',
    };

    if ('search' in params) {
      newFilters.search = params.search;
    }

    if ('category' in params) {
      const selectedCategoriesSlugs = isArray(params.category) ? params.category : [params.category];
      newFilters.categories = this._categoriesIdsFromSlugs(selectedCategoriesSlugs);
    }

    if ('provider' in params) {
      const selectedProvidersSlugs = isArray(params.provider) ? params.provider : [params.provider];
      newFilters.providers = this._providersIdsFromSlugs(selectedProvidersSlugs);
    }

    if ('attribute' in params) {
      const selectedAttributesSlugs = isArray(params.attribute) ? params.attribute : [params.attribute];
      newFilters.attributes = this._attributesIdsFromSlugs(selectedAttributesSlugs);
    }

    this._filters = newFilters;
    this._filters$.next(newFilters);
    return newFilters;
  }

  /**
   * When single category is applied in filters
   * @param filters
   */
  private _isSingleCategoryApplied(filters: Filters): boolean {
    if (filters?.categories?.length) {
      return filters?.categories?.length === 1 && !filters?.providers?.length && !filters?.attributes?.length && !filters?.search;
    } else {
      return false;
    }
  }

  /**
   * When single attribute is applied in filters
   * @param filters
   */
  private _isSingleAttributeApplied(filters: Filters): boolean {
    if (filters?.attributes?.length) {
      return filters?.attributes?.length === 1 && !filters?.categories?.length && !filters?.providers?.length && !filters?.search;
    } else {
      return false;
    }
  }

  /**
   * When single provider is applied in filters
   * @param filters
   */
  private _isSingleProviderApplied(filters: Filters): boolean {
    if (filters?.providers?.length) {
      return filters?.providers?.length === 1 && !filters?.categories?.length && !filters?.attributes?.length && !filters?.search;
    } else {
      return false;
    }
  }

  private _resolveParamsForMultipleChoice(newFiltersParams) {
    const changedQueryParams: any = {};

    if ('categories' in newFiltersParams) {
      changedQueryParams.category = this._categoriesSlugsFromIds(newFiltersParams?.categories);
    } else if (this._isSingleCategoryApplied(this._filters)) {
      changedQueryParams.category = this._categoriesSlugsFromIds(this._filters?.categories);
    }

    if ('providers' in newFiltersParams) {
      changedQueryParams.provider = this._providersSlugsFromIds(newFiltersParams?.providers);
    } else if (this._isSingleProviderApplied(this._filters)) {
      changedQueryParams.provider = (changedQueryParams.provider || []).concat(this._providersSlugsFromIds(this._filters?.providers));
    }

    if ('attributes' in newFiltersParams) {
      changedQueryParams.attribute = this._attributesSlugsFromIds(newFiltersParams?.attributes)?.length
        ? this._attributesSlugsFromIds(newFiltersParams?.attributes)
        : null;
    } else if (this._isSingleAttributeApplied(this._filters)) {
      changedQueryParams.attribute = (changedQueryParams?.attribute || []).concat(this._attributesSlugsFromIds(this._filters?.attributes));
    }

    if ('search' in newFiltersParams) {
      changedQueryParams.search = newFiltersParams.search || null;
    }

    return changedQueryParams;
  }

  private _resolveParamsForSingleChoice(data: any): any {
    return {
      queryParams: data,
      queryParamsHandling: 'merge',
    };
  }

  /**
   * Merge attr with categories and created options for multiple checkbox
   * @private
   */
  private _createCategoriesOptions(): Observable<Option[]> {
    return this._category.categoryAttrList$.pipe(
      map(list =>
        this.user.auth
          ? list.categoryList.concat(list.attributeList).map((item: GameCategoryType) => {
              return {
                value: item.id,
                label: item.name,
              };
            })
          : this._filtersService
              .excludeUsedByKey('id', [GameCategory.LAST_PLAYED_ID, GameCategory.FAVORITE_ID], list.categoryList.concat(list.attributeList))
              .map((item: GameCategoryType) => {
                return {
                  value: item.id,
                  label: item.name,
                };
              }),
      ),
    );
  }

  /**
   * Create provider options for multiple checkbox
   * @private
   */
  private _createProvidersOptions(): Observable<Option[]> {
    return this._providers.list$.pipe(map(list => list.map(item => ({ ...item, value: item.id, label: item.name }))));
  }

  /**
   * Get ids from slugs for categories
   * @param slugs
   */
  private _categoriesIdsFromSlugs(slugs: string[]): number[] {
    return slugs
      .map(slug => {
        const cat = this._category.filterCategoryList.find(category => category.slug === slug);
        return cat ? cat.id : null;
      })
      .filter(id => !!id) as number[];
  }

  /**
   * Get slugs from ids for categories
   * @param ids
   */
  private _categoriesSlugsFromIds(ids: number[]): string[] {
    if (ids?.length) {
      return ids.map(id => this._category.filterCategoryList.find(category => category.id === id)?.slug).filter(slug => !!slug);
    } else {
      return [];
    }
  }

  /**
   * Get slugs from ids for providers
   * @param ids
   */
  private _providersSlugsFromIds(ids: number[]): string[] {
    if (ids?.length) {
      return ids.map(id => this._providers.getById(id)?.alias).filter(slug => !!slug);
    } else {
      return [];
    }
  }

  /**
   * Get ids from slugs for providers
   * @param slugs
   */
  private _providersIdsFromSlugs(slugs: string[]): number[] {
    if (slugs) {
      return slugs
        .map(slug => {
          const provider = this._providers.getByAlias(slug);
          return provider ? +provider.id : null;
        })
        .filter(id => !!id);
    }
  }

  /**
   * Get slugs from ids for attributes
   * @param ids
   */
  private _attributesSlugsFromIds(ids: number[]): string[] {
    if (ids) {
      return ids
        .map(id => {
          const attr = this._category.getAttrById(id);
          return attr ? attr.slug : null;
        })
        .filter(slug => !!slug);
    }
  }

  /**
   * Get ids from slugs for categories
   * @param slugs
   */
  private _attributesIdsFromSlugs(slugs: string[]): number[] {
    return slugs
      .map(slug => {
        const attr = this._category.getAttrByAlias(slug);
        return attr ? +attr.id : null;
      })
      .filter(id => !!id) as number[];
  }

  /**
   * Set applied filters
   * @private
   */
  private _settAppliedFilters() {
    this.form.get('category[]').setValue(this.filter.categories.concat(this.filter.attributes));
    this.form.get('provider[]').setValue(this.filter.providers);
  }
}
